import React, { useState } from "react";
import { ConfigProvider, Typography, Card, Avatar } from "antd";
import { PhoneOutlined, UserOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const { Text } = Typography;
const ACCENT = "#185FA5";

const STAGES = [
  { key: "active", label: "Active", color: "#185FA5" },
  { key: "converted", label: "Converted", color: "#2E9E5B" },
  { key: "lost", label: "Lost", color: "#C0392B" },
];

// Sample data — replace with API response once backend is ready.
// Shape expected from backend eventually: { id, name, company, phone, value, stage }
const SAMPLE_LEADS = [
  { id: "lead-1", name: "Rohit Sharma", company: "Nexus Traders", phone: "+91 98110 22334", value: 45000, stage: "active" },
  { id: "lead-2", name: "Priya Mehta", company: "Orbit Logistics", phone: "+91 90210 11223", value: 120000, stage: "active" },
  { id: "lead-3", name: "Aman Verma", company: "Zenith Foods", phone: "+91 88990 44556", value: 32000, stage: "active" },
  { id: "lead-4", name: "Sneha Kapoor", company: "Bluepeak Media", phone: "+91 99887 66554", value: 78000, stage: "active" },
  { id: "lead-5", name: "Karan Malhotra", company: "Ironclad Steel", phone: "+91 97654 32109", value: 210000, stage: "converted" },
  { id: "lead-6", name: "Neha Joshi", company: "Sunrise Apparels", phone: "+91 96543 21098", value: 56000, stage: "converted" },
  { id: "lead-7", name: "Vikram Rathore", company: "Delta Motors", phone: "+91 95432 10987", value: 300000, stage: "converted" },
  { id: "lead-8", name: "Anjali Singh", company: "Coral Interiors", phone: "+91 94321 09876", value: 18000, stage: "lost" },
  { id: "lead-9", name: "Rahul Nair", company: "Vertex Software", phone: "+91 93210 98765", value: 65000, stage: "lost" },
];

const currencyFmt = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

// ============ draggable lead card ============
function LeadCard({ lead }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    marginBottom: 10,
    cursor: "grab",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card size="small" styles={{ body: { padding: 12 } }} style={{ borderRadius: 8, boxShadow: "0 1px 3px rgba(20,30,50,0.10)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Avatar size={26} icon={<UserOutlined />} style={{ backgroundColor: "#185FA5", flexShrink: 0 }} />
          <Text strong style={{ fontSize: 13, color: "#2A3441" }}>
            {lead.name}
          </Text>
        </div>
        <div style={{ fontSize: 12, color: "#6B7686", marginBottom: 4 }}>{lead.company}</div>
        <div style={{ fontSize: 11.5, color: "#8A94A3", display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
          <PhoneOutlined style={{ fontSize: 11 }} />
          {lead.phone}
        </div>
        <Text strong style={{ fontSize: 13, color: "#185FA5" }}>
          {currencyFmt.format(lead.value)}
        </Text>
      </Card>
    </div>
  );
}

// ============ droppable column ============
function PipelineColumn({ stage, leads }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  const totalValue = leads.reduce((sum, l) => sum + (l.value || 0), 0);

  return (
    <div
      style={{
        width: 300,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        // No vh math here — height comes from the parent flex row,
        // which itself is bounded by the page-level flex column below.
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ padding: "10px 12px", background: "#fff", borderRadius: "8px 8px 0 0", border: "1px solid #E4E8EE", borderBottom: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color, flexShrink: 0 }} />
          <Text strong style={{ fontSize: 13.5, color: "#2A3441" }}>
            {stage.label}
          </Text>
          <Text style={{ fontSize: 12, color: "#8A94A3", marginLeft: "auto" }}>{leads.length}</Text>
        </div>
        <div style={{ fontSize: 11.5, color: "#8A94A3", marginTop: 2 }}>{currencyFmt.format(totalValue)}</div>
      </div>

      <div
        ref={setNodeRef}
        data-pipeline-scroll="true"
        style={{
          flex: 1,
          minHeight: 0, // critical: lets this flex child actually shrink and scroll
          padding: 10,
          background: isOver ? "#EAF2FA" : "#F4F6F9",
          border: "1px solid #E4E8EE",
          borderRadius: "0 0 8px 8px",
          overflowY: "auto",
          transition: "background 120ms",
        }}
      >
        <SortableContext items={leads.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
          {leads.length === 0 && (
            <div style={{ fontSize: 12, color: "#C7CFDA", textAlign: "center", padding: "24px 0" }}>Drop leads here</div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}

// ============ main board ============
export default function PipelineBoard() {
  const [leads, setLeads] = useState(SAMPLE_LEADS);
  const [activeLead, setActiveLead] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getLeadsByStage = (stageKey) => leads.filter((l) => l.stage === stageKey);

  const findContainer = (id) => {
    // id is either a stage key (dropped on empty column) or a lead id
    if (STAGES.some((s) => s.key === id)) return id;
    const lead = leads.find((l) => l.id === id);
    return lead?.stage;
  };

  const handleDragStart = (event) => {
    const lead = leads.find((l) => l.id === event.active.id);
    setActiveLead(lead || null);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setLeads((prev) => {
      const activeIndex = prev.findIndex((l) => l.id === active.id);
      if (activeIndex === -1) return prev;
      const updated = [...prev];
      updated[activeIndex] = { ...updated[activeIndex], stage: overContainer };
      return updated;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveLead(null);
    if (!over) return;

    const overContainer = findContainer(over.id);
    if (!overContainer) return;

    if (active.id !== over.id) {
      setLeads((prev) => {
        const containerItems = prev.filter((l) => l.stage === overContainer);
        const oldIndex = containerItems.findIndex((l) => l.id === active.id);
        const newIndex = containerItems.findIndex((l) => l.id === over.id);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;

        const reordered = arrayMove(containerItems, oldIndex, newIndex);
        const others = prev.filter((l) => l.stage !== overContainer);
        return [...others, ...reordered];
      });
    }

    // TODO: once backend is ready — call apiUpdateLeadStage(active.id, overContainer) here
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: ACCENT, borderRadius: 6 } }}>
      <div
        style={{
          fontFamily: "Arial, sans-serif",
          background: "#F4F6F9",
          height: "100vh", // owns exactly the viewport — not minHeight, so it can never grow taller
          padding: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // this element is the scroll boundary; nothing outside it should scroll
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: 18, flexShrink: 0 }}>
          <Text strong style={{ fontSize: 18, color: "#0F3D6B" }}>
            Leads Pipeline
          </Text>
          <div style={{ fontSize: 12.5, color: "#8A94A3", marginTop: 2 }}>
            Drag a lead card between stages to update its status.
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          autoScroll={{
            // dnd-kit normally walks UP the DOM to find any scrollable
            // ancestor (including the window/document) to auto-scroll
            // while dragging near an edge. That's what was scrolling the
            // whole page. Restrict it to ONLY our tagged column list —
            // never the page itself.
            canScroll: (element) => element?.dataset?.pipelineScroll === "true",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 18,
              alignItems: "flex-start",
              overflowX: "auto",
              flex: 1, // takes all remaining height after the title block above
              minHeight: 0, // lets this row actually shrink instead of overflowing the flex parent
            }}
          >
            {STAGES.map((stage) => (
              <PipelineColumn key={stage.key} stage={stage} leads={getLeadsByStage(stage.key)} />
            ))}
          </div>

          <DragOverlay>{activeLead ? <LeadCard lead={activeLead} /> : null}</DragOverlay>
        </DndContext>
      </div>
    </ConfigProvider>
  );
}
